import {
  BoundTextField,
  Button,
  Css,
  HasIdAndName,
  SuperDrawerContent,
  SuperDrawerHeader,
  TabsWithContent,
  useSessionStorage,
} from "@homebound/beam";
import { Observer } from "mobx-react";
import type { NamedFragment } from "src/fragments/commonFragments.generated";
import {
  MaterialCatalog_ItemFragment,
  MaterialCatalog_MaterialBrandFragment,
  useMaterialCatalogMetadataQuery,
} from "src/routes/libraries/material-catalog/MaterialCatalog.generated";
import { queryResult } from "src/utils";
import { MaterialListingForm } from "../material-listing-form/MaterialListingForm";
import { MaterialDesignNotesTab, MaterialDesignNotesTabIndicator } from "../MaterialDesignNotesTab";
import { MaterialListingFormHistoryTab } from "../MaterialListingFormHistoryTab";
import { MaterialSuperDrawerContextProvider, useMaterialSuperDrawerContext } from "./MaterialSuperDrawerContext";
import { MaterialSuperDrawerView } from "./MaterialSuperDrawerView";

export type MaterialSuperDrawerProps = { variantId: string; readOnly?: boolean; isOptionCatalogV2Enabled?: boolean };

export function MaterialSuperDrawer({
  variantId,
  readOnly = true,
  isOptionCatalogV2Enabled,
}: MaterialSuperDrawerProps) {
  const query = useMaterialCatalogMetadataQuery();

  return queryResult(query, ({ materialBrands, items, materialVariantTags, markets }) => (
    <MaterialSuperDrawerContextProvider variantId={variantId} readOnly={readOnly}>
      <MaterialSuperDrawerContent
        items={items}
        brands={materialBrands}
        tags={materialVariantTags}
        markets={markets}
        isOptionCatalogV2Enabled={isOptionCatalogV2Enabled}
      />
    </MaterialSuperDrawerContextProvider>
  ));
}

type MaterialSuperDrawerContentProps = {
  items: MaterialCatalog_ItemFragment[];
  brands: MaterialCatalog_MaterialBrandFragment[];
  tags: NamedFragment[];
  markets: HasIdAndName[];
  isOptionCatalogV2Enabled?: boolean;
};

function MaterialSuperDrawerContent({
  items,
  brands,
  tags,
  isOptionCatalogV2Enabled,
  markets,
}: MaterialSuperDrawerContentProps) {
  const { listing, formState, save, cancel, readOnly, toggleReadOnly } = useMaterialSuperDrawerContext();

  const [selectedTab, setSelectedTab] = useSessionStorage("materialSuperDrawerTab", "details");

  const detailsTab = {
    name: "Details",
    value: "details",
    render: () => (
      <MaterialListingForm
        isNew={false}
        formState={formState}
        canEditItem={listing?.canEditItem}
        items={items}
        brands={brands}
        tags={tags}
        markets={markets}
        isOptionCatalogV2Enabled={isOptionCatalogV2Enabled}
      />
    ),
  };

  const designNotesTab = {
    name: "Design Notes",
    value: "designNotes",
    endAdornment: <MaterialDesignNotesTabIndicator />,
    render: () => <MaterialDesignNotesTab />,
  };

  const historyTab = {
    name: "History",
    value: "history",
    render: () => <MaterialListingFormHistoryTab materialListingId={listing?.id!} />,
  };

  const tabs = [detailsTab];
  if (isOptionCatalogV2Enabled) tabs.push(historyTab);
  tabs.push(designNotesTab);

  return (
    <SuperDrawerContent>
      <>
        <SuperDrawerHeader
          title={
            readOnly ? (
              <div css={Css.df.fdc.gap1.$}>
                <h1 css={Css.lg.$}>{listing?.name}</h1>
                <div css={Css.df.gapPx(4).md.$}>
                  <span>{listing?.item.name}</span>
                  {listing?.brand && <>By {listing.brand.name}</>}
                </div>
              </div>
            ) : (
              <div css={Css.w100.df.aic.gap3.$}>
                <div css={Css.fg1.$}>
                  <BoundTextField field={formState.name} label="Name" labelStyle="hidden" fullWidth />
                </div>
                <Observer>
                  {() => (
                    <div css={Css.df.gap1.pr1.$}>
                      <Button label="Cancel" variant="quaternary" onClick={cancel} />
                      <Button
                        label="Save"
                        disabled={!formState.dirty || !formState.valid}
                        variant="primary"
                        onClick={save}
                      />
                    </div>
                  )}
                </Observer>
              </div>
            )
          }
          hideControls
          right={
            <div css={Css.df.aic.gap1.$}>
              {readOnly && <Button label="Edit" variant="secondary" onClick={toggleReadOnly} />}
            </div>
          }
        />
      </>
      {readOnly ? (
        <MaterialSuperDrawerView items={items} brands={brands} />
      ) : (
        <>
          <TabsWithContent tabs={tabs} selected={selectedTab} onChange={setSelectedTab} />
        </>
      )}
    </SuperDrawerContent>
  );
}
